import { useState, useEffect } from "react";
import SuccessToast from "../../components/toasts/success-toast";

const PREFS_KEY = "ah_user_preferences";

const defaultPrefs = {
  emailNotifications: true,
  inviteAlerts: true,
  scoreReportAlerts: true,
  weeklyDigest: false,
  language: "en",
  timezone: "Asia/Kolkata",
  density: "comfortable",
};

const notificationOptions = [
  {
    key: "emailNotifications",
    title: "Email notifications",
    desc: "Receive product updates and activity emails",
  },
  {
    key: "inviteAlerts",
    title: "Invite alerts",
    desc: "Get notified when a candidate accepts an interview invite",
  },
  {
    key: "scoreReportAlerts",
    title: "Score report alerts",
    desc: "Get notified when interview scoring is completed",
  },
  {
    key: "weeklyDigest",
    title: "Weekly digest",
    desc: "Summary of activity every Monday morning",
  },
];

const Preferences = () => {
  const [prefs, setPrefs] = useState(defaultPrefs);

  useEffect(() => {
    document.title = "Preferences - Ample Hire";
  }, []);

  useEffect(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(PREFS_KEY) || "null");
      if (stored) setPrefs({ ...defaultPrefs, ...stored });
    } catch (e) {}
  }, []);

  const update = (key, value) => setPrefs((p) => ({ ...p, [key]: value }));

  const handleSave = () => {
    localStorage.setItem(PREFS_KEY, JSON.stringify(prefs));
    SuccessToast("Preferences saved");
  };

  const handleReset = () => {
    setPrefs(defaultPrefs);
    localStorage.removeItem(PREFS_KEY);
    SuccessToast("Preferences reset to defaults");
  };

  return (
    <div className="p-6 bg-vf-bg min-h-full">
      {/* Page header */}
      <div className="mb-6">
        <h1 className="text-2xl font-semibold text-vf-base">Preferences</h1>
        <p className="mt-1 text-sm text-vf-muted">
          Customize how Ample Hire works for you.
        </p>
      </div>

      {/* Notifications */}
      <div className="card p-6 mb-6">
        <h2 className="text-base font-semibold text-vf-base mb-4">
          Notifications
        </h2>
        <div className="divide-y divide-vf-base/10">
          {notificationOptions.map((opt) => (
            <label
              key={opt.key}
              className="flex items-center justify-between py-4 first:pt-0 last:pb-0 cursor-pointer"
            >
              <div className="pr-4">
                <div className="text-sm font-medium text-vf-base">
                  {opt.title}
                </div>
                <div className="text-xs text-vf-muted mt-0.5">{opt.desc}</div>
              </div>
              <input
                type="checkbox"
                className="checkbox"
                checked={prefs[opt.key]}
                onChange={(e) => update(opt.key, e.target.checked)}
              />
            </label>
          ))}
        </div>
      </div>

      {/* Regional */}
      <div className="card p-6 mb-6">
        <h2 className="text-base font-semibold text-vf-base mb-4">Regional</h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          <div>
            <label className="input-label mb-1.5 block">Language</label>
            <select
              className="select w-full"
              value={prefs.language}
              onChange={(e) => update("language", e.target.value)}
            >
              <option value="en">English</option>
              <option value="hi">Hindi</option>
              <option value="es">Spanish</option>
              <option value="fr">French</option>
            </select>
          </div>
          <div>
            <label className="input-label mb-1.5 block">Timezone</label>
            <select
              className="select w-full"
              value={prefs.timezone}
              onChange={(e) => update("timezone", e.target.value)}
            >
              <option value="Asia/Kolkata">Asia / Kolkata (IST)</option>
              <option value="UTC">UTC</option>
              <option value="America/New_York">America / New York</option>
              <option value="Europe/London">Europe / London</option>
            </select>
          </div>
          <div>
            <label className="input-label mb-1.5 block">Display density</label>
            <select
              className="select w-full"
              value={prefs.density}
              onChange={(e) => update("density", e.target.value)}
            >
              <option value="comfortable">Comfortable</option>
              <option value="compact">Compact</option>
            </select>
          </div>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3">
        <button className="btn-ghost btn-sm" onClick={handleReset}>
          Reset To Defaults
        </button>
        <button className="btn-primary btn-sm" onClick={handleSave}>
          Save Preferences
        </button>
      </div>
    </div>
  );
};

export default Preferences;
